import React, { useState, useEffect } from "react";
import { Search, Filter, ChevronDown } from "lucide-react";
import axios from "axios";

const FilterSection = ({
  onFilterChange,
  categories,
  selectedCategoryId,
  selectedSubcategoryId,
}) => {
  const [subcategories, setSubcategories] = useState([]);
  const [localFilters, setLocalFilters] = useState({
    searchQuery: "",
    categoryId: selectedCategoryId,
    subcategoryId: selectedSubcategoryId,
    sortOrder: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (localFilters.categoryId) {
        try {
          const subResponse = await axios.get(
            `https://api.elza2.store/api/v1/categories/${localFilters.categoryId}/subcategories`
          );
          setSubcategories(subResponse.data.subCategories);
        } catch (error) {
          console.error("Failed to fetch data", error);
          setSubcategories([]);
        }
      }
    };

    fetchData();
  }, [localFilters.categoryId]);

  useEffect(() => {
    setLocalFilters((prev) => ({
      ...prev,
      categoryId: selectedCategoryId || "",
      subcategoryId: selectedSubcategoryId || "",
    }));
  }, [selectedCategoryId, selectedSubcategoryId]);

  const handleLocalFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };

    if (key === "categoryId") {
      newFilters.subcategoryId = ""; // Reset subcategory when category changes
    }

    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters); // Only apply filters when button is clicked
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
      <div className="flex items-center gap-3 mb-5 border-b pb-3">
        <Filter className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-800">Refine Search</h2>
      </div>

      <div className="space-y-5">
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            value={localFilters.searchQuery}
            onChange={(e) =>
              handleLocalFilterChange("searchQuery", e.target.value)
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Category
          </label>
          <div className="relative">
            <select
              className="w-full p-2.5 text-sm border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={localFilters.categoryId}
              onChange={(e) =>
                handleLocalFilterChange("categoryId", e.target.value)
              }
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {localFilters.categoryId && subcategories.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Subcategory
            </label>
            <div className="relative">
              <select
                className="w-full p-2.5 text-sm border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={localFilters.subcategoryId}
                onChange={(e) =>
                  handleLocalFilterChange("subcategoryId", e.target.value)
                }
              >
                <option value="">All Subcategories</option>
                {subcategories.map((subcategory) => (
                  <option key={subcategory._id} value={subcategory._id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Sort By
          </label>
          <div className="relative">
            <select
              className="w-full p-2.5 text-sm border-2 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={localFilters.sortOrder}
              onChange={(e) =>
                handleLocalFilterChange("sortOrder", e.target.value)
              }
            >
              <option value="">Default Sorting</option>
              <option value="price">Price: Low to High</option>
              <option value="-price">Price: High to Low</option>
              <option value="title">Name: A to Z</option>
              <option value="-title">Name: Z to A</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div className="mt-5">
          <button
            onClick={handleApplyFilters}
            className="w-full p-2.5 explore-btn rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
